import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from '@/alerts/index';
import { llmSmartSearchV1 } from '@/api/requests/llm/llm';
import { useServiceToken } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { ResponseError, convertKeysToCamelCase } from '@/utils/index';

export enum SmartSearchQueryTypesEnum {
  allReportTypesSmartSearchV1 = 'allReportTypesSmartSearchV1',
  personSmartSearchV1 = 'personSmartSearchV1',
  vehicleSmartSearchV1 = 'vehicleSmartSearchV1',
}

interface LLMSmartSearchV1HookProps {
  enabled: boolean;
  searchValue: string;
  shouldSuspendExecution?: boolean;
}

export const useLLMSmartSearchV1 = (
  queryType: keyof typeof SmartSearchQueryTypesEnum,
  llmPersonHookData: LLMSmartSearchV1HookProps
) => {
  const baseQueryKey = SmartSearchQueryTypesEnum[queryType];
  const { enabled, searchValue, shouldSuspendExecution } = llmPersonHookData;
  const { data: tokenData } = useServiceToken('llm');
  const { enqueueSnackbar } = useSnackbar();
  const { logError } = useAppConfigContext();
  const useLLMSmartSearchV1MaxRetries = 2;

  const useQueryKey = `${baseQueryKey}-${searchValue}`;

  const handleSmartSearchError = (error: ResponseError) => {
    if (error.response?.status === 429) {
      // OpenAI quota or rate limited exceeded error
      logError(`${baseQueryKey}: rate limit error`, error);
    } else {
      logError(`${baseQueryKey}: generation failed`, error);
    }

    enqueueSnackbar('Smart Search generation failed', {
      variant: 'error',
      autoHideDuration: 3000,
    });
  };

  const fetchSmartSearch = async () => {
    if (shouldSuspendExecution) return null;

    const smartSearchFnMap = {
      [SmartSearchQueryTypesEnum.allReportTypesSmartSearchV1]:
        llmSmartSearchV1.createSmartSearchV1,
      [SmartSearchQueryTypesEnum.personSmartSearchV1]:
        llmSmartSearchV1.createPersonSmartSearchV1,
      [SmartSearchQueryTypesEnum.vehicleSmartSearchV1]:
        llmSmartSearchV1.createVehicleSmartSearchV1,
    };

    const smartSearchFn = smartSearchFnMap[queryType];

    if (!smartSearchFn) {
      throw new Error('Invalid Smart Search type requested');
    }

    const response = await smartSearchFn(tokenData.token, {
      search: searchValue,
    });

    return Promise.resolve(convertKeysToCamelCase(response));
  };

  const result = useQuery({
    queryKey: [useQueryKey],
    queryFn: fetchSmartSearch,
    retry: (failureCount, error: ResponseError) => {
      // Do not retry if OpenAI quota or rate limited exceeded
      if (error?.response?.status === 429) {
        return false;
      }

      const maxRetries = useLLMSmartSearchV1MaxRetries || 1;
      return failureCount < maxRetries;
    },
    cacheTime: 0,
    enabled: Boolean(tokenData?.token) && enabled,
    onError: (error: ResponseError) => {
      handleSmartSearchError(error);
    },
  });

  return result;
};
